import React, { useEffect, useRef } from "react";
import "./AnimatedBackground.css";

// ─── tuning ───────────────────────────────────────────────
const NUM_NODES      = 75;
const CONNECT_DIST   = 155;   // px — max distance to draw a line
const NODE_SPEED     = 0.18;  // px per frame (very slow drift)
const NODE_RADIUS    = 2.2;
const COLOR_BASE     = [255, 255, 255]; // white
const COLOR_ACCENT   = [255, 255, 255]; // white
// ─────────────────────────────────────────────────────────

const AnimatedBackground = () => {
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);
  const nodesRef  = useRef([]);

  useEffect(() => {
    if (window.matchMedia("(max-width: 768px)").matches) return;

    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");

    let width, height;

    const initNodes = () => {
      nodesRef.current = Array.from({ length: NUM_NODES }, () => {
        const angle = Math.random() * Math.PI * 2;
        const speed = NODE_SPEED * (0.4 + Math.random() * 0.6);
        return {
          x:     Math.random() * width,
          y:     Math.random() * height,
          vx:    Math.cos(angle) * speed,
          vy:    Math.sin(angle) * speed,
          phase: Math.random() * Math.PI * 2,
          size:  0.6 + Math.random() * 0.8, // scale multiplier
        };
      });
    };

    const resize = () => {
      width  = canvas.width  = window.innerWidth;
      height = canvas.height = window.innerHeight;
      initNodes();
    };

    resize();
    window.addEventListener("resize", resize);

    const startTime = performance.now();

    const draw = (ts) => {
      const t = (ts - startTime) / 1000;

      ctx.clearRect(0, 0, width, height);

      const nodes = nodesRef.current;

      // ── move nodes (wrap at edges) ──────────────────────
      nodes.forEach(n => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < -20)       n.x = width  + 20;
        if (n.x > width + 20) n.x = -20;
        if (n.y < -20)       n.y = height + 20;
        if (n.y > height + 20) n.y = -20;
      });

      // ── draw connections ────────────────────────────────
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx   = nodes[i].x - nodes[j].x;
          const dy   = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist >= CONNECT_DIST) continue;

          const alpha = (1 - dist / CONNECT_DIST) * 0.45;
          // gradient line: accent → base
          const grad = ctx.createLinearGradient(
            nodes[i].x, nodes[i].y, nodes[j].x, nodes[j].y
          );
          grad.addColorStop(0, `rgba(${COLOR_ACCENT.join(",")},${alpha})`);
          grad.addColorStop(1, `rgba(${COLOR_BASE.join(",")},${alpha * 0.5})`);

          ctx.beginPath();
          ctx.moveTo(nodes[i].x, nodes[i].y);
          ctx.lineTo(nodes[j].x, nodes[j].y);
          ctx.strokeStyle = grad;
          ctx.lineWidth   = 0.9;
          ctx.stroke();
        }
      }

      // ── draw nodes ──────────────────────────────────────
      nodes.forEach(n => {
        const pulse  = 0.75 + 0.25 * Math.sin(t * 1.6 + n.phase);
        const r      = NODE_RADIUS * n.size * pulse;
        const alpha  = 0.65 + 0.35 * pulse;

        // soft outer glow
        ctx.beginPath();
        ctx.arc(n.x, n.y, r * 5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${COLOR_ACCENT.join(",")},0.10)`;
        ctx.fill();

        // mid ring
        ctx.beginPath();
        ctx.arc(n.x, n.y, r * 2.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${COLOR_BASE.join(",")},0.22)`;
        ctx.fill();

        // core dot
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${COLOR_ACCENT.join(",")},${alpha})`;
        ctx.fill();
      });

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div className="portfolio-background" aria-hidden="true">
      <canvas ref={canvasRef} className="bg-canvas" />
      <div className="bg-vignette" />
    </div>
  );
};

export default AnimatedBackground;
